import { notifyError } from "@src/shared/notify";
import { Page } from "@src/shared/pages";
import { PublicRoute, Route } from "@src/sudozi-schemas/api-route-schemas";
import { ZodVoid, z } from "zod";

export type ApiCallParams<T extends Route | PublicRoute> =
  T["params"] extends ZodVoid
    ? T["body"] extends ZodVoid
      ? T["query"] extends ZodVoid
        ? {
            route: T;
            body?: never;
            params?: never;
            query?: never;
          }
        : {
            route: T;
            body?: never;
            params?: never;
            query: z.infer<T["query"]>;
          }
      : T["query"] extends ZodVoid
      ? {
          route: T;
          body: z.infer<T["body"]>;
          params?: never;
          query?: never;
        }
      : {
          route: T;
          body: z.infer<T["body"]>;
          params?: never;
          query: z.infer<T["query"]>;
        }
    : T["body"] extends ZodVoid
    ? T["query"] extends ZodVoid
      ? {
          route: T;
          body?: never;
          params: z.infer<T["params"]>;
          query?: never;
        }
      : {
          route: T;
          body?: never;
          params: z.infer<T["params"]>;
          query: z.infer<T["query"]>;
        }
    : T["query"] extends ZodVoid
    ? {
        route: T;
        body: z.infer<T["body"]>;
        params: z.infer<T["params"]>;
        query?: never;
      }
    : {
        route: T;
        body: z.infer<T["body"]>;
        params: z.infer<T["params"]>;
        query: z.infer<T["query"]>;
      };

export async function sudoziFetch<
  R extends Route | PublicRoute,
  O extends R["output"],
>({ route, body, params, query }: ApiCallParams<R>): Promise<z.infer<O>> {
  const outputSchema = route.output;

  try {
    const result = await sudoziFetchBase({ route, body, params, query });
    const json = await result.json();

    if (outputSchema && !(outputSchema instanceof z.ZodVoid)) {
      return outputSchema.parse(json);
    }
  } catch (err) {
    if (err instanceof Error && "message" in err) {
      notifyError(err.message);
    } else {
      notifyError("An unknown error occurred. Please try again.");
    }
  }
}

export async function sudoziFetchBase({
  route,
  body,
  params,
  query,
}: {
  route: Route | PublicRoute;
  body?: Record<string, unknown> | Record<string, unknown>[] | void;
  params?: Record<string, string> | void;
  query?: Record<
    string,
    | string
    | string[]
    | null
    | {
        by: string[];
        dir: ("ASC" | "DESC")[];
      }
  > | void;
}) {
  let url = route.path as string;

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      url = url.replace(`:${key}`, value as string);
    }
  }

  if (query) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (typeof value === "string") {
        params.append(key, value ?? "");
      }

      if (Array.isArray(value)) {
        if (!value.length) {
          params.append(`${key}[]`, "");
        }
        value.forEach((v) => {
          params.append(`${key}[]`, v ?? "");
        });
      } else if (typeof value === "object" && value) {
        value.by.forEach((by) => {
          params.append("orderBy[by][]", by);
        });
        value.dir.forEach((dir) => {
          params.append("orderBy[dir][]", dir);
        });
      }
    }
    url = `${url}?${params.toString()}`;
  }

  const result = await fetch(url, {
    method: route.method,
    headers: {
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (result.status === 401 && !location.href.startsWith(Page.LOGIN)) {
    window.location.href = "/login";
  }

  if (!result.ok) {
    let message = "";
    try {
      message = (await result.json()).message;
    } catch (e) {
      message = result.statusText;
    }
    throw new Error(
      message?.trim() ? message : `API ${route.method} to ${url} failed`,
    );
  }

  return result;
}
